import React, { memo, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import i18next from 'i18next';
import { useNavigate } from 'react-router';
import { TrainingPlanDto } from '../../../../../shared/dtos/trainingplan.dto';
import ConfirmationModal from '../../../components/ConfirmationModal';
import PrimaryButton from '../../../components/PrimaryButton';
import SecondaryButton from '../../../components/SecondaryButton';
import { TrainingPlanRequest } from '../../../../../shared/requests/trainingPlan.request';
import SaveAndPublishModal from '../modals/SaveAndPublishModal';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import TrainingPlanForm, { InvalidTrainingFocus } from './TrainingPlanForm';
import { TrainingPlanStatus, TrainingType } from '../../../../../shared/utils/enums';
import { createTrainingPlanValidationSchema } from "../trainingPlan.validation";
import TrainingPlanStatusBadge from "./TrainingPlanStatusBadge";
import {
  TrainingFormValues, TrainingPlanFormValues,
  toTrainingFormValues, toEnduranceTrainingFormValues, splitTrainingRequests
} from "../trainingPlan.types";
import { emitSuccessNotification } from '../../../components/Notification';
import UnsavedChangesPrompt from './UnsavedChangesPrompt';

type CurrentTrainingPlanState = {
  id?: string;
  endDate?: string;
  unpublishedDate?: string;
  status?: TrainingPlanStatus;
};

const toCurrentTrainingPlanState = (trainingPlan?: TrainingPlanDto): CurrentTrainingPlanState => ({
  id: trainingPlan?.id,
  endDate: trainingPlan?.endDate ? trainingPlan.endDate : undefined,
  unpublishedDate: trainingPlan?.unpublishedAt ? trainingPlan.unpublishedAt : undefined,
  status: trainingPlan?.status,
});

type TrainingPlanDetailsProps = {
  patient: any;
  therapyId: string;
  trainingPlan?: TrainingPlanDto;
};

const TrainingPlanDetails = ({ patient, trainingPlan, therapyId }: TrainingPlanDetailsProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCreateNewConfirmModal, setShowCreateNewConfirmModal] = useState(false);

  const [currentTrainingPlan, setCurrentTrainingPlan] = useState<CurrentTrainingPlanState>(() => toCurrentTrainingPlanState(trainingPlan));

  const [isPublishUpdating, setIsPublishUpdating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [invalidTrainingFocus, setInvalidTrainingFocus] = useState<InvalidTrainingFocus>();

  const navigate = useNavigate();

  const focusFirstInvalidTraining = (trainingErrors: unknown): void => {
    if (!Array.isArray(trainingErrors)) {
      return;
    }

    const index: number = trainingErrors.findIndex((trainingError: unknown) => Boolean(trainingError));

    if (index < 0) {
      return;
    }

    setInvalidTrainingFocus((previous?: InvalidTrainingFocus) => ({ index, attempt: (previous?.attempt ?? 0) + 1 }));
  };

  const formikRef = useRef<FormikProps<TrainingPlanFormValues>>(null);

  const resetDirtyBaseline = (): void => {
    const formik = formikRef.current;
    if (formik) {
      formik.resetForm({ values: formik.values });
    }
  };

  const hasTrainingPlan = !!currentTrainingPlan.id;
  const hasNoTrainingPlan = !hasTrainingPlan;
  const isDraft = currentTrainingPlan.status === TrainingPlanStatus.DRAFT;
  const isPublished = currentTrainingPlan.status === TrainingPlanStatus.PUBLISHED;

  const showSaveButton = hasNoTrainingPlan || isDraft;
  const showUnpublishButton = isPublished;

  useEffect(() => {
    setCurrentTrainingPlan(toCurrentTrainingPlanState(trainingPlan));
  }, [trainingPlan]);

  const initialValues = useMemo<TrainingPlanFormValues>(() => {
    const normalItems = (trainingPlan?.trainings ?? []).map((training) => ({
      order: training.order,
      form: toTrainingFormValues(training),
    }));

    const enduranceItems = (trainingPlan?.enduranceTrainings ?? []).map((training) => ({
      order: training.order,
      form: toEnduranceTrainingFormValues(training),
    }));

    const merged = [...normalItems, ...enduranceItems]
      .sort((a, b) => a.order - b.order)
      .map((item) => item.form);

    const defaultTraining: TrainingFormValues = {
      name: `${i18next.t('trainingPlan.newTabTraining')} 1`,
      workoutsPerWeek: 3,
      workoutPause: 60,
      exercises: [],
      trainingKind: TrainingType.STRENGTH,
      enduranceTrainingType: undefined,
    };

    return {
      trainings: merged.length ? merged : [defaultTraining],
    };
  }, [trainingPlan]);

  const buildTrainingPlanRequest = (values: TrainingPlanFormValues): TrainingPlanRequest => {
    const { trainings, enduranceTrainings } = splitTrainingRequests(values.trainings);

    return {
      therapyId,
      trainings,
      enduranceTrainings,
    };
  };

  const createDraftTrainingPlan = (values: TrainingPlanFormValues): Promise<TrainingPlanDto> => {
    return authFetch('/api/v1/trainingplan/', {
      method: 'POST',
      body: buildTrainingPlanRequest(values),
    });
  };

  const updateDraftTrainingPlan = (values: TrainingPlanFormValues, id: string): Promise<TrainingPlanDto> => {
    return authFetch(`/api/v1/trainingplan/${id}`, {
      method: 'PATCH',
      body: buildTrainingPlanRequest(values),
    });
  };

  const publishTrainingPlanById = (trainingPlanId: string, date: Date): Promise<TrainingPlanDto> => {
    return authFetch(`/api/v1/trainingplan/${trainingPlanId}/publish`, {
      method: 'PATCH',
      body: { endDate: date.toISOString() },
    });
  };

  const unPublishTrainingPlanById = (trainingPlanId: string): Promise<TrainingPlanDto> => {
    return authFetch(`/api/v1/trainingplan/${trainingPlanId}/unpublish`, {
      method: 'PATCH',
    });
  };

  const getPrimaryActionLabel = (): string => {
    if (isPublishUpdating || isSaving) {
      return i18next.t('generic.loading');
    }

    if (hasNoTrainingPlan) {
      return i18next.t('trainingPlan.saveAndPublish');
    }

    if (isDraft) {
      return i18next.t('trainingPlan.publish');
    }

    return i18next.t('trainingPlan.createNew');
  };

  const unpublishTrainingPlan = async (showToast = true): Promise<void> => {
    if (!currentTrainingPlan.id) {
      return;
    }

    const previousTrainingPlan = currentTrainingPlan;

    setShowConfirmModal(false);
    setIsPublishUpdating(true);

    try {
      const trainingPlan = await unPublishTrainingPlanById(currentTrainingPlan.id);
      setCurrentTrainingPlan(toCurrentTrainingPlanState(trainingPlan));
      if (showToast) {
        emitSuccessNotification(i18next.t('trainingPlan.toast.unpublished'));
      }
      resetDirtyBaseline();
    } catch (error) {
      setCurrentTrainingPlan(previousTrainingPlan);

      throw error;
    } finally {
      setIsPublishUpdating(false);
    }
  };

  const publishTrainingPlan = async (values: TrainingPlanFormValues, date: Date): Promise<void> => {
    if (!currentTrainingPlan.id) {
      return;
    }

    const previousTrainingPlan = currentTrainingPlan;

    setShowSaveModal(false);
    setIsPublishUpdating(true);

    try {
      await updateDraftTrainingPlan(values, currentTrainingPlan.id);
      const trainingPlan = await publishTrainingPlanById(currentTrainingPlan.id, date);
      setCurrentTrainingPlan(toCurrentTrainingPlanState(trainingPlan));
      emitSuccessNotification(i18next.t('trainingPlan.toast.published'));
      resetDirtyBaseline();
    } catch (error) {
      setCurrentTrainingPlan(previousTrainingPlan);

      throw error;
    } finally {
      setIsPublishUpdating(false);
    }
  };

  const saveTrainingPlan = async (
    values: TrainingPlanFormValues,
    formikHelpers?: FormikHelpers<TrainingPlanFormValues>
  ): Promise<void> => {
    const previousTrainingPlan = currentTrainingPlan;
    const isCreateNew = isPublished && currentTrainingPlan.id;

    setIsSaving(true);

    try {
      if (isDraft && currentTrainingPlan.id) {
        await updateDraftTrainingPlan(values, currentTrainingPlan.id);
        emitSuccessNotification(i18next.t('trainingPlan.toast.saved'));
        resetDirtyBaseline();
        return;
      }

      if (isCreateNew) {
        await unPublishTrainingPlanById(currentTrainingPlan.id!);
      }

      const trainingPlan = await createDraftTrainingPlan(values);
      setCurrentTrainingPlan(toCurrentTrainingPlanState(trainingPlan));
      emitSuccessNotification(i18next.t('trainingPlan.toast.saved'));
      resetDirtyBaseline();
    } catch (error) {
      setCurrentTrainingPlan(previousTrainingPlan);
      throw error;
    } finally {
      setIsSaving(false);
      formikHelpers?.setSubmitting(false);
    }
  };

  const saveAndPublishTrainingPlan = async (values: TrainingPlanFormValues, date: Date): Promise<void> => {
    const previousTrainingPlan = currentTrainingPlan;

    setShowSaveModal(false);
    setIsPublishUpdating(true);

    try {
      const createdTrainingPlan = await createDraftTrainingPlan(values);

      setCurrentTrainingPlan(toCurrentTrainingPlanState(createdTrainingPlan));

      const publishedTrainingPlan = await publishTrainingPlanById(createdTrainingPlan.id, date);

      setCurrentTrainingPlan(toCurrentTrainingPlanState(publishedTrainingPlan));
      emitSuccessNotification(i18next.t('trainingPlan.toast.published'));
      resetDirtyBaseline();
    } catch (error) {
      setCurrentTrainingPlan(previousTrainingPlan);
      throw error;
    } finally {
      setIsPublishUpdating(false);
    }
  };

  return (
    <Formik<TrainingPlanFormValues>
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={createTrainingPlanValidationSchema()}
      onSubmit={saveTrainingPlan}
    >
      {({ values, submitForm, isSubmitting, validateForm, setTouched, dirty }) => {
        const isBusy = isSubmitting || isPublishUpdating || isSaving;
        const isPrimaryDisabled = isBusy || (isPublished && !dirty);

        const touchAllFields = (): void => {
          const touched: Record<string, boolean | Record<string, boolean | Record<string, boolean>[]>[]> = {};

          values.trainings.forEach((training, _) => {
            const trainingTouched: Record<string, boolean | Record<string, boolean>[]> = {
              name: true,
              workoutsPerWeek: true,
              workoutPause: true,
              enduranceTrainingType: true,
            };

            trainingTouched.exercises = training.exercises.map(() => ({
              name: true,
              description: true,
              sets: true,
              time: true,
              repetition: true,
            }));

            touched.trainings = touched.trainings || [];
            (touched.trainings as Record<string, boolean | Record<string, boolean>[]>[]).push(trainingTouched);
          });

          void setTouched(touched as any);
        };

        const validateAndTouch = async (): Promise<boolean> => {
          const errors = await validateForm();

          if (Object.keys(errors).length > 0) {
            touchAllFields();
            focusFirstInvalidTraining(errors.trainings);
            return false;
          }

          return true;
        };

        const validateAndSubmit = async (): Promise<void> => {
          if (await validateAndTouch()) {
            await submitForm();
          }
        };

        const handlePrimaryButtonClick = async (): Promise<void> => {
          if (!(await validateAndTouch())) {
            return;
          }

          if (hasNoTrainingPlan || isDraft) {
            setShowSaveModal(true);
            return;
          }

          if (isPublished) {
            setShowCreateNewConfirmModal(true);
            return;
          }

          await submitForm();
        };

        return (
          <Container className="py-4">
            <UnsavedChangesPrompt />
            <div className="text-reset">
              <div className="px-4 py-3 text-white rounded-3 mb-3" style={{ backgroundColor: '#234a68' }}>
                <div className="d-flex align-items-center gap-3">
                  <Button
                    variant="outline-light"
                    className="d-inline-flex align-items-center gap-2 flex-shrink-0 text-nowrap"
                    onClick={() => navigate(-1)}
                    aria-label={i18next.t('generic.back')}
                  >
                    <i className="fas fa-angle-left" aria-hidden="true" />
                    <span className="d-none d-sm-inline">{i18next.t('generic.back')}</span>
                  </Button>

                  <div className="d-flex flex-grow-1 overflow-hidden align-items-center gap-3">
                    <span className="fw-semibold fs-4 text-truncate">
                      <span className="d-none d-sm-inline">
                        {i18next.t('trainingPlan.trainingPlanFor')}{' '}
                      </span>
                      {patient.firstName} {patient.lastName}
                    </span>

                    {currentTrainingPlan.status ?
                      <TrainingPlanStatusBadge
                        status={currentTrainingPlan.status}
                        endDate={currentTrainingPlan.endDate}
                        unpublishedDate={currentTrainingPlan.unpublishedDate}
                      /> : null
                    }

                  </div>

                  <div className="d-flex align-items-center gap-3 ms-auto flex-shrink-0">
                    {showSaveButton && (
                      <SecondaryButton onClick={() => validateAndSubmit()} disabled={isBusy}>
                        {isSaving ? i18next.t('generic.loading') : i18next.t('trainingPlan.saveAsDraft')}
                      </SecondaryButton>
                    )}
                    {showUnpublishButton && (
                      <SecondaryButton onClick={() => setShowConfirmModal(true)} disabled={isBusy}>
                        {i18next.t('trainingPlan.unpublish.button')}
                      </SecondaryButton>
                    )}

                    <PrimaryButton onClick={() => void handlePrimaryButtonClick()} disabled={isPrimaryDisabled}>
                      {getPrimaryActionLabel()}
                    </PrimaryButton>
                  </div>
                </div>
              </div>

              <div className="bg-white border rounded-3 shadow-sm overflow-hidden">
                <TrainingPlanForm invalidTrainingFocus={invalidTrainingFocus}/>
              </div>
            </div>

            <ConfirmationModal
              show={showConfirmModal}
              title={i18next.t('trainingPlan.unpublish.title')}
              body={<p>{i18next.t('trainingPlan.unpublish.body')}</p>}
              buttonPositive={i18next.t('trainingPlan.unpublish.button')}
              buttonNegative={i18next.t('generic.cancel')}
              onPositive={unpublishTrainingPlan}
              onNegative={() => setShowConfirmModal(false)}
            />

            <ConfirmationModal
              show={showCreateNewConfirmModal}
              title={i18next.t('trainingPlan.createNewConfirm.title')}
              body={<p>{i18next.t('trainingPlan.createNewConfirm.body')}</p>}
              buttonPositive={i18next.t('trainingPlan.createNewConfirm.button')}
              buttonNegative={i18next.t('generic.cancel')}
              onPositive={() => {
                setShowCreateNewConfirmModal(false);
                void validateAndSubmit();
              }}
              onNegative={() => setShowCreateNewConfirmModal(false)}
            />

            <SaveAndPublishModal
              show={showSaveModal}
              onPositive={(date) => {
                if (hasNoTrainingPlan) {
                  return saveAndPublishTrainingPlan(values, date);
                }

                return publishTrainingPlan(values, date);
              }}
              onNegative={() => setShowSaveModal(false)}
              initialValues={{
                date: currentTrainingPlan.endDate ? new Date(currentTrainingPlan.endDate) : new Date(),
              }}
            />
          </Container>
        )
      }}
    </Formik>
  );
};

export default memo(TrainingPlanDetails);
